/**
 * Auto-run of the post-mapping analyses the moment a final mapping card lands,
 * instead of waiting for the user to click each tab.
 *
 * Two kinds of artifact, warmed differently:
 *  - Trends (ratios) and Working Capital (CCC) are deterministic — their GETs
 *    compute on first read and cache server-side (Redis), so a single
 *    fire-and-forget GET is the whole warm-up.
 *  - Insights and the EBITDA bridge are LLM passes behind POST endpoints. We
 *    GET the cached artifact first and only when it's absent start the POST
 *    through the generationJobs registry, under the SAME job key the tab
 *    uses — so opening the tab mid-generation re-attaches to this run, the
 *    export popover reports it as 'generating', and nothing ever fires twice.
 *
 * The card renders on the mapping_complete SSE event, a beat before the
 * session's terminal status write lands, and the generate endpoints 409 in
 * that window — the POSTs retry briefly to absorb it.
 *
 * A failure recorded by an earlier attempt (getJobError) is respected: the
 * expensive pass is never silently re-fired; the tab's "Try again" clears it.
 */
import {
  generateAgentEbitdaBridge,
  generateAgentInsights,
  getAgentEbitdaBridge,
  getAgentInsights,
  getAgentRatios,
  getWorkingCapital,
} from './agentApi';
import { getInflightJob, getJobError, startJob } from './generationJobs';

// One warm-up per artifact per page load. Every result is cached server-side,
// so re-warming would be harmless — this just keeps card re-mounts (session
// switching, strict-mode double mounts) from re-running the GET/POST dance.
const warmed = new Set<string>();

const ACTIVE_RETRY_DELAYS_MS = [2000, 4000, 8000];

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function generateWhenTerminal<T>(generate: () => Promise<T>): Promise<T> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await generate();
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      if (status !== 409 || attempt >= ACTIVE_RETRY_DELAYS_MS.length) throw err;
      await sleep(ACTIVE_RETRY_DELAYS_MS[attempt]);
    }
  }
}

function warmGenerated(
  key: string,
  read: () => Promise<unknown>,
  generate: () => Promise<unknown>,
): void {
  if (warmed.has(key)) return;
  warmed.add(key);
  if (getInflightJob(key) || getJobError(key)) return;
  (async () => {
    const cached = await read();
    if (cached != null) return;
    if (getInflightJob(key)) return; // a tab beat us to it during the GET
    await startJob(key, () => generateWhenTerminal(generate));
  })().catch(() => {
    // Fire-and-forget: a generation failure is recorded in the job registry
    // and surfaces (with a Try again) when the tab is opened.
  });
}

function warmComputed(key: string, read: () => Promise<unknown>): void {
  if (warmed.has(key)) return;
  warmed.add(key);
  read().catch(() => {
    // Cache-priming only — the tab re-GETs on mount and owns error display.
  });
}

export function prefetchAnalyses({
  pnlSessionId,
  insightsSessionId,
  wcSessionId,
}: {
  /** P&L session — trends (ratios) and the EBITDA bridge live here. */
  pnlSessionId?: string | null;
  /** Insights host — the PARENT for unified sessions, the session itself standalone. */
  insightsSessionId?: string | null;
  /** Unified parent — working capital (CCC) joins both children. Omit standalone. */
  wcSessionId?: string | null;
}): void {
  if (pnlSessionId) {
    warmComputed(`ratios:${pnlSessionId}`, () => getAgentRatios(pnlSessionId));
    warmGenerated(
      `ebitda-bridge:${pnlSessionId}`,
      () => getAgentEbitdaBridge(pnlSessionId),
      () => generateAgentEbitdaBridge(pnlSessionId, { force: false }),
    );
  }
  if (wcSessionId) {
    warmComputed(`wc:${wcSessionId}`, () => getWorkingCapital(wcSessionId));
  }
  if (insightsSessionId) {
    warmGenerated(
      `insights:${insightsSessionId}`,
      () => getAgentInsights(insightsSessionId),
      () => generateAgentInsights(insightsSessionId, { force: false }),
    );
  }
}
